<template>
  <b-container>
    <AccordionText
      v-for="accordion in accordions"
      :key="accordion.id"
      :id="accordion.id"
      :title="accordion.title"
      :text="accordion.text"
    ></AccordionText>
  </b-container>
</template>

<script>
import AccordionText from "../../components/AccordionText";

export default {
  data() {
    return {
      accordions: [
        {
          title: "Enfant",
          id: "enfant",
          text: `
Une bonne vision est indispensable pour le développement global de votre enfant. Elle se construit durant les premières années de vie. Plus un trouble visuel est dépisté tôt, plus le traitement sera efficace.

Nous prenons en charge les dépistages visuels des enfants, du nourrisson jusqu’à l’adolescence.

#### Comment se passe le bilan?

Le bilan se déroule en 3 étapes chez l’enfant résumées ainsi:

- Dépistage du strabisme et examen oculomoteur,
- Dépistage d’un défaut optique (besoin de lunettes pour de la myopie, de l’hypermétropie, de l’astigmatisme)
- Examen de l’acuité visuelle pour dépister les amblyopies (oeil plus faible)

#### Quel est son but?

L’ensemble permet de d’étudier la maturité visuelle et de mettre en place des traitements le plus rapidement possible s’ils sont utiles (port de lunettes, caches oculaires, rééducation) pour qu’il y ait un minimum de répercussion dans ses apprentissages et son développement

#### Quand faut il le faire?

L'[HAS](https://www.has-sante.fr/jcms/c_272178/fr/depistage-precoce-des-troubles-de-la-fonction-visuelle-chez-l-enfant-pour-prevenir-l-amblyopie) préconise un contrôle vers l’âge de 9 mois (“bébévision”) vers 2,5/3 ans et vers l’âge de 6 ans avant l’apprentissage de la lecture et de l’écriture.

![Infographie vision bébé](/prisesEnChargeImages/image33.jpg)

#### Quels sont les signes d’alerte pour consulter ?

*Dès les premières semaines*:

- absence de clignement à la lumière

*Après 4 mois*:

-   L’enfant de suit pas un objet qui se déplace lentement devant ses yeux à 20cm
-   Absence de contact visuel avec ses proches
-   Absence de sourire
-   Strabisme permanent
    
*Après 9 mois*:

-   N’attrappe pas les objets (doudou, biberon, jouets…)
-   Tête inclinée en permanence (torticolis)
-   Strabisme intermittent ou permanent
    

*Après l’acquisition de la marche*:

-   Se cogne, et/ou tombe souvent
-   Plisse des yeux, cligne, ferme un oeil au soleil
-   Ne regarde pas les livres, les images
-   Peu d'intérêt pour les jeux de constructions (cubes encastrements)
    
*Après 3 ans*

- Plaintes visuelles
- Fatigue visuelle
- Maux de tête
- Plissement des yeux, rapprochement des écrans
- Brûlures ou picotements oculaires,
- Vision floue ou double intermittente (peu décrit chez les enfants),
- Fermeture d’un oeil à la fixation
- Difficultés de concentration
- Troubles des apprentissages (difficultés en lecture, orthographe, écriture avec suivi de lignes compliqués, confusions, inversions, omissions, sauts de lignes ou de mots; difficultés avec les tableaux à doubles entrées, ou en repérage et organisation dans une page)

#### Autres motifs de consultation

##### Antécédents familiaux (parents, grands parents, frères et soeurs, cousins…):
-   Strabisme
-   Port de lunettes avant l’âge de 10/12ans
-   Vision faible d’un oeil (amblyopie)
-   Consanguinité
    

#####  Antécédents personnels (grossesse et accouchement)
-   Grossesse pathologique
-   Consommation d’alcool, tabac, stupéfiants pendant la grossesse
-   Prématurité (< 32 SA)
-   Souffrance cérébrale ou réanimation
-   Petits poids de naissance (inférieur à 1500g)`,
        },
        {
          title: "Adulte",
          id: "adulte",
          text: `
Vous avez un doute sur votre vue et vous voulez la faire contrôler? Deux cas de figure s’offrent à vous

-   Vous êtes déjà porteur de lunettes et vous souhaitez [faire renouveler votre ordonnance de verres correcteurs ](https://www.service-public.fr/particuliers/actualites/A14028) sans attendre votre prochain rendez-vous chez l’ophtalmologiste. Cliquez sur la section "Renouvellement optique" pour en savoir plus.
    

-   Vous ne portez pas de lunettes et souhaitez simplement savoir si votre vision vous fait défaut. Les délais de RDV des ophtalmologistes sont trop longs, ou bien ils ne prennent plus de nouveaux patients? Demandez une prescription d’ “examen de vue” à votre médecin traitant et prenez un rendez-vous par internet "examen de vue/réfraction".
    

Les défauts de vision: Imaginez votre oeil comme un appareil photo avec une image qui doit se former sur la rétine pour que votre vue soit nette. En cas de problème réfractif, l’image ne se forme pas sur la rétine et l’oeil voit flou ou bien fatigue.

![Les différentes amétropies](/prisesEnChargeImages/image30.jpg)

#### La myopie

L’oeil est trop convergent. l’image se forme en avant de la rétine. On voit flou au loin et net de près . Cela oblige le myope à plisser les yeux lorsqu’il essaie de voir au loin sans lunettes.

#### L’hypermétropie

L’oeil n’est pas assez convergent. L’image se forme en arrière de la rétine. La vue devrait être floue MAIS le cristallin va rattraper ce problème en accommodant. L’oeil auto-corrige ainsi son problème de mise au point et arrive à voir net. Cependant cela est coûteux et provoque une fatigue visuelle, une rougeur oculaire (asthénopie) , des céphalées fronto-temporales surtout après avoir fait un effort visuel. Parfois, l’hypermétropie est tellement forte, le cristallin enclenche autant d’accommodation qui elle même va enclencher un fort réflexe de convergence et un strabisme apparaît. C’est le strabisme accommodatif. Une paire de lunettes corrigeant l’hypermétropie permet de faire ainsi disparaître le strabisme.

#### L’astigmatisme

La cornée (hublot transparent par lequel rentre la lumière dans l’oeil) est en théorie sphérique (ronde) comme un ballon. En cas d’astigmatisme elle a la forme d’un ballon de rugby. Cette irrégularité va provoquer une vision dite “baveuse”. L’oeil va essayer d’accommoder pour minimiser le flou . L’astigmatisme provoque de ce fait une vision un peu trouble et une fatigue visuelle accompagnée ou non de rougeur oculaire et céphalées fronto-temporales.

#### La presbytie

Au cours de notre vie, notre capacité d’accommodation décroît régulièrement jusqu’à devenir critique aux alentours de 45 ans. L’accommodation qui sert à faire la mise au point et à voir net de près, diminue. La vision de près devient de plus en plus fatigante voire floue.`,
        },
        {
          title:
            "Dépistage trouble de la vision des couleurs / Test d'Ishihara",
          id: "couleur",
          text: `Le cabinet est équipé d’un Test d’Ishihara qui permet de tester la vision des couleurs, il complète ainsi le bilan ophtalmologique pour permettre le dépistage de certaines pathologies oculaires appelées dyschromatopsies (comme le daltonisme).
          ![Test d'Ishara](/prisesEnChargeImages/image20.jpg)`,
        },
      ],
    };
  },
  components: {
    AccordionText,
  },
};
</script>
